import { useState, useEffect, useContext, useMemo } from "react";
import { createPortal } from "react-dom";
import { FiSearch, FiX, FiBarChart2 } from "react-icons/fi";
import { GraphContext } from "../../../../../../contexts/GraphContext";
import { findNodeStats } from "./GraphNodeUtils";

export const AddChildTagModal = ({
  onClose,
  onAddChildTag,
  selectedTag,
  tagData,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [newValueName, setNewValueName] = useState("");
  const [filteredValues, setFilteredValues] = useState([]);

  const { hierarchyStats, discoveryGraphData } = useContext(GraphContext);

  // Get values with their stats, sorted by file count
  const valuesWithStats = useMemo(() => {
    if (!tagData || !discoveryGraphData) return [];

    let current = discoveryGraphData;

    // Navigate to the tag's data
    for (const pathItem of tagData.nodePath) {
      current = current[pathItem];
      if (!current) return [];
    }

    // Extract values (excluding TagAvailableValues key)
    const values = Object.keys(current).filter(
      (key) => key !== "TagAvailableValues",
    );

    // Get stats for each value
    const valuesWithStats = values.map((value) => {
      const valuePath = [...tagData.nodePath, value];
      const stats = findNodeStats(hierarchyStats, valuePath);
      return {
        value,
        fileCount: stats?.file_count || 0,
        percentage: stats?.percentage || 0,
        isOpenEnded: value === "<ANY>",
        isNotFound: value === "Not found",
      };
    });

    // Sort by file count (descending), but keep special values at the end
    return valuesWithStats.sort((a, b) => {
      if (a.isOpenEnded) return 1;
      if (b.isOpenEnded) return -1;
      if (a.isNotFound) return 1;
      if (b.isNotFound) return -1;
      return b.fileCount - a.fileCount;
    });
  }, [tagData, discoveryGraphData, hierarchyStats]);

  // Filter values based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredValues(valuesWithStats);
    } else {
      const query = searchQuery.toLowerCase().trim();
      setFilteredValues(
        valuesWithStats.filter((item) =>
          item.value.toLowerCase().includes(query),
        ),
      );
    }
  }, [searchQuery, valuesWithStats]);

  const handleAddChildTagToValue = (value) => {
    if (onAddChildTag) {
      onAddChildTag(value);
    }
    onClose();
  };

  const handleCreateValueAndAddChildTag = () => {
    if (newValueName.trim()) {
      if (onAddChildTag) {
        onAddChildTag(newValueName.trim());
      }
      onClose();
    }
  };

  const totalValues = valuesWithStats.length;
  const filteredCount = filteredValues.length;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary/10 to-primary/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-primary"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Create Nested Tag
                </h2>
                <p className="text-sm text-gray-600">
                  Choose a value under{" "}
                  <span className="font-medium text-primary">
                    {selectedTag}
                  </span>{" "}
                  to create a sub-tag and build your tag hierarchy
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search values..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-500">
              {searchQuery ? (
                <>
                  Showing {filteredCount} of {totalValues} values
                </>
              ) : (
                <>{totalValues} values available</>
              )}
            </span>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <FiBarChart2 className="w-3 h-3" />
              <span>Sorted by usage</span>
            </div>
          </div>
        </div>

        {/* Values List */}
        <div className="flex-1 overflow-y-auto max-h-[50vh]">
          <div>
            {filteredValues.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <FiSearch className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-lg font-medium">No values found</p>
                <p className="text-sm">
                  {searchQuery
                    ? "Try adjusting your search"
                    : "No values available for child tag placement"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredValues.map(
                  ({
                    value,
                    fileCount,
                    percentage,
                    isOpenEnded,
                    isNotFound,
                  }) => (
                    <div
                      key={value}
                      className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                        isOpenEnded
                          ? "bg-blue-50/50"
                          : isNotFound
                            ? "bg-gray-50/50"
                            : fileCount > 0
                              ? "bg-green-50/30"
                              : "bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3
                              className={`font-medium truncate ${
                                isOpenEnded
                                  ? "text-blue-700"
                                  : isNotFound
                                    ? "text-gray-600"
                                    : "text-gray-900"
                              }`}
                            >
                              {value}
                            </h3>
                            {isOpenEnded && (
                              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                                Open-ended
                              </span>
                            )}
                            {isNotFound && (
                              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                                Not found
                              </span>
                            )}
                          </div>

                          {fileCount > 0 && (
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">
                                  {fileCount} files ({percentage.toFixed(1)}%)
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-primary to-primary-dark transition-all duration-500"
                                  style={{
                                    width: `${Math.min(percentage, 100)}%`,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="ml-4">
                          <button
                            onClick={() => handleAddChildTagToValue(value)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg hover:from-primary-dark hover:to-primary-darker focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                          >
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                            </svg>
                            <span className="font-medium">Create Sub-Tag</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        </div>

        {/* Create New Value Section */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gradient-to-r from-primary/5 to-primary/10 rounded-b-xl">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-primary"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              <h3 className="font-medium text-gray-900">Create Custom Value</h3>
            </div>
            <p className="text-sm text-gray-600 ml-6">
              Define a new value and create a nested tag structure beneath it.
            </p>

            <div className="flex gap-3 ml-6">
              <input
                type="text"
                placeholder="Enter custom value name..."
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                value={newValueName}
                onChange={(e) => setNewValueName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newValueName.trim()) {
                    handleCreateValueAndAddChildTag();
                  }
                }}
              />
              <button
                onClick={handleCreateValueAndAddChildTag}
                disabled={!newValueName.trim()}
                className="px-4 py-2.5 bg-gradient-to-r from-primary-dark to-primary-darker text-white rounded-lg hover:from-primary-darker hover:to-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none focus:ring-2 focus:ring-primary-dark/20 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105 flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                <span className="font-medium">Add Value</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Keep the old export for backward compatibility but make it use the new component
export const ValueSelectionModal = (props) => {
  // If this is being used for child tag functionality, use the new component
  if (props.allowChildTagAddition && props.tagData) {
    return (
      <AddChildTagModal
        onClose={props.onClose}
        onAddChildTag={props.onAddChildTag}
        selectedTag={props.selectedTag}
        tagData={props.tagData}
      />
    );
  }

  // Otherwise, fall back to a simplified version for regular value selection
  // (This maintains backward compatibility while focusing on the child tag functionality)
  return null;
};
